import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiFetch } from '@/lib/api/client';
import { sessionQueryKey, useSession } from '@/lib/auth/session';
import { closedKeysFor } from '@/lib/menu/menuGate';

/**
 * 오픈 게이트 — 「아직 열지 않은 메뉴」 (백엔드 `MenuGate` · V153 · 사용자 지시 2026-08-28).
 *
 * ## 두 갈래로 읽는다
 *
 * - {@link useClosedMenuKeys} — **화면이 감출 때**. 세션에 실려 온 목록을 보므로 요청이 늘지 않고,
 *   로그인한 전원이 쓴다(사이드바·라우트 가드·헤더 버튼).
 * - {@link useMenuGateSettings} — **관리 모달이 고칠 때**. 서버에서 새로 읽는다. 세션은 로그인
 *   시점에 굳은 값이라, 방금 저장한 결과를 모달이 그 값으로 확인하면 한 박자 늦게 보인다.
 *
 * ## 권한이 아니다
 *
 * 감추는 일일 뿐이다. 실제 차단은 서버가 매 요청마다 한다(`UserMenuPermissionResolver`).
 * 이 파일을 통째로 지워도 닫아 둔 API 가 열려서는 안 된다.
 */

export const menuGateQueryKey = ['menu-gate'] as const;

/** 지금 이 사용자에게 닫혀 있는 메뉴 키. 관리자에게는 언제나 빈 집합이다(`menuGate.ts`). */
export function useClosedMenuKeys(): Set<string> {
  const { data: session } = useSession();
  return closedKeysFor(session);
}

/**
 * 관리 모달용 — 서버가 들고 있는 「닫힌 목록」 그 자체. `settings_menus` 조회 권한이 필요하다.
 *
 * <p>{@code enabled} 로 끌 수 있다. 세션의 목록({@link useClosedMenuKeys})은 관리자에게 <b>언제나
 * 비어 있어서</b> 「지금 무엇이 닫혀 있나」를 물을 수 없다 — 현장 조회의 권한 점검표가 이 훅을
 * 부르는 이유다. 그 표는 관리자에게만 뜨므로, 아닌 사람에게는 질의도 걸지 않는다.
 */
export function useMenuGateSettings(enabled = true) {
  return useQuery({
    queryKey: menuGateQueryKey,
    queryFn: () => apiFetch<string[]>('/v1/menu-gate'),
    enabled,
  });
}

/**
 * 닫혀 있어야 할 목록 **전체**를 저장한다(전체 교체 — 빠진 키는 열린다).
 *
 * 저장 뒤 세션까지 무효화한다. 사이드바·라우트 가드가 보는 것은 세션에 실린 목록이라,
 * 여기만 갱신하면 모달의 스위치는 바뀌었는데 왼쪽 메뉴는 그대로인 화면이 된다.
 */
export function useSaveMenuGate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (closedMenuKeys: string[]) =>
      apiFetch<string[]>('/v1/menu-gate', { method: 'PUT', body: { closedMenuKeys } }),
    onSuccess: (saved) => {
      queryClient.setQueryData(menuGateQueryKey, saved);
      queryClient.invalidateQueries({ queryKey: sessionQueryKey });
    },
  });
}

/**
 * <b>이 키들만 연다</b> — 나머지는 건드리지 않는다 (사용자 지시 2026-09-11).
 *
 * <h2>왜 위의 전체 저장으로 하지 않나</h2>
 * {@link useSaveMenuGate} 는 <b>전체 교체</b>다. 오픈 관리 창은 초안 방식이라, 오픈 하나를 즉시
 * 반영하려고 그것을 부르면 <b>같은 창에서 손대 둔 다른 스위치까지 함께 저장된다</b> — 사람은
 * 메뉴 하나를 열었다고 생각하는데 아직 정하지 않은 것들이 같이 나간다.
 *
 * <p>오픈이 즉시여야 하는 이유는 순서 때문이다: 안내 공지를 먼저 올리고 <b>그 다음에</b> 연다
 * (사용자 결정 2026-09-11). 공지는 이미 전 직원에게 나갔는데 오픈은 [저장] 을 눌러야 되는
 * 상태로 두면, 창을 그냥 닫은 날 <b>열리지도 않은 메뉴를 알리는 공지</b>만 남는다.
 *
 * <p>닫는 쪽은 그대로 초안이다. 닫는 것은 되돌리기 쉬운 일이고, 한 번에 여러 개를 내리는 것이
 * 실제 쓰임이라 모아서 저장하는 편이 맞다.
 *
 * @return 갱신된 <b>닫힌 키 목록 전체</b>(전체 저장과 같은 모양)
 */
export function useOpenMenuGateKeys() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (menuKeys: string[]) =>
      apiFetch<string[]>('/v1/menu-gate/open', { method: 'POST', body: { menuKeys } }),
    onSuccess: (saved) => {
      queryClient.setQueryData(menuGateQueryKey, saved);
      // 사이드바·라우트 가드가 보는 것은 세션에 실린 목록이다 — 여기만 갱신하면 방금 연 메뉴가
      // 다시 로그인할 때까지 왼쪽에 서지 않는다.
      queryClient.invalidateQueries({ queryKey: sessionQueryKey });
    },
  });
}
